import { useEffect, useState } from "react";
import api, { apiError, fmtUSD } from "@/lib/api";
import { Panel, Table, Empty } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const NUMERIC_RULES = [
  ["max_purchase_per_txn", "Max $ per transaction"], ["max_daily_purchase_total", "Max daily purchases ($)"],
  ["max_weekly_purchase_total", "Max weekly volume ($)"], ["max_asset_concentration_pct", "Max asset concentration (%)"],
  ["min_operating_cash", "Min operating cash ($)"], ["min_opportunity_reserve", "Min opportunity reserve ($)"],
  ["max_portfolio_drawdown_pct", "Max portfolio drawdown (%)"], ["max_experimental_pct", "Max experimental (%)"],
  ["max_spread_pct", "Max spread/slippage (%)"], ["large_purchase_threshold", "Large purchase threshold ($)"],
  ["cooldown_after_large_purchase_min", "Cooldown after large purchase (min)"],
];

export default function Risk() {
  const [rules, setRules] = useState(null);
  const [events, setEvents] = useState([]);
  const [approved, setApproved] = useState("");
  const [blocked, setBlocked] = useState("");

  const load = () => {
    api.get("/risk/rules").then((r) => {
      setRules(r.data);
      setApproved((r.data.approved_assets || []).join(", "));
      setBlocked((r.data.blocked_assets || []).join(", "));
    }).catch(() => {});
    api.get("/risk/events").then((r) => setEvents(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = { ...rules,
        approved_assets: approved.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        blocked_assets: blocked.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) };
      await api.put("/risk/rules", payload);
      toast.success("Risk Governor rules updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!rules) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="risk-page">
      <h2 className="font-display text-xl font-bold">Risk Governor</h2>
      <p className="text-xs text-slate-500">No transaction reaches execution unless it passes every rule below. Rejections are logged permanently.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Rules" testId="risk-rules-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {NUMERIC_RULES.map(([key, label]) => (
              <div key={key}>
                <div className="metric-label mb-1">{label}</div>
                <Input type="number" step="any" data-testid={`risk-rule-${key}`} value={rules[key]}
                  onChange={(e) => setRules({ ...rules, [key]: parseFloat(e.target.value) || 0 })}
                  className="bg-[#121316] border-[#2D323E] font-mono h-8 text-xs" />
              </div>
            ))}
            <div>
              <div className="metric-label mb-1">Approved Whitelist</div>
              <Input data-testid="risk-rule-approved" value={approved} onChange={(e) => setApproved(e.target.value)}
                className="bg-[#121316] border-[#2D323E] font-mono h-8 text-xs" placeholder="BTC, XRP" />
            </div>
            <div>
              <div className="metric-label mb-1">Blocked Blacklist</div>
              <Input data-testid="risk-rule-blocked" value={blocked} onChange={(e) => setBlocked(e.target.value)}
                className="bg-[#121316] border-[#2D323E] font-mono h-8 text-xs" />
            </div>
          </div>
          <Button data-testid="risk-save-button" onClick={save} className="mt-4 bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">SAVE RULES</Button>
          <p className="text-[10px] text-slate-600 mt-3">Hard prohibitions (never allowed): withdrawals, external transfers initiated by AI, margin borrowing, leveraged derivatives, options, prediction markets, automatic promotion of unapproved assets.</p>
        </Panel>
        <Panel title="Rejected Actions" testId="risk-events-panel">
          {events.length === 0 ? <Empty>No rejected actions. The Risk Governor has not blocked anything yet.</Empty> : (
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} className="border-l-2 border-l-red-600 bg-[#121316] rounded-r-sm px-4 py-3" data-testid={`risk-event-${e.id.slice(0, 8)}`}>
                  <div className="flex justify-between text-[10px] tracking-widest uppercase text-slate-500 mb-1">
                    <span className="font-mono">{new Date(e.timestamp).toLocaleString()}</span>
                    <span>{e.side} {e.asset} · requested {fmtUSD(e.requested_amount)} · allowed {fmtUSD(e.allowed_amount)}</span>
                  </div>
                  {e.violations.map((v, i) => <p key={i} className="text-xs text-red-300">✕ {v.rule}: {v.detail}</p>)}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
